/**
 * Lazy loader for camera-controls
 * 
 * camera-controls is lazily loaded to reduce initial bundle size.
 * The module is only loaded when 3D scene features are actually used.
 * 
 * NOTE: Three.js subset is passed from the caller to avoid bundling
 * Three.js into the camera-controls chunk.
 */

// Type for CameraControls (imported dynamically)
export type CameraControlsType = typeof import('camera-controls').default;
export type CameraControlsInstance = InstanceType<CameraControlsType>;

// Cached module reference
let cachedCameraControls: CameraControlsType | null = null;
let installPromise: Promise<CameraControlsType> | null = null;

// Store the THREE subset for installation
let threeSubset: any = null;

/**
 * Initialize the Three.js subset required by camera-controls.
 * Must be called before loadCameraControls().
 * 
 * @param subset - Object containing Three.js classes: Vector2, Vector3, Vector4, 
 *                 Quaternion, Matrix4, Spherical, Box3, Sphere, Raycaster
 */
export const initCameraControlsThree = (subset: {
	Vector2: any;
	Vector3: any;
	Vector4: any;
	Quaternion: any;
	Matrix4: any;
	Spherical: any;
	Box3: any;
	Sphere: any;
	Raycaster: any;
}): void => {
	threeSubset = subset;
};

/**
 * Lazy load and install camera-controls
 * Returns cached instance if already loaded
 * 
 * @returns Promise resolving to CameraControls class
 */
export const loadCameraControls = async (): Promise<CameraControlsType> => {
	// Return cached if already loaded
	if (cachedCameraControls) {
		return cachedCameraControls;
	}

	// If already loading, wait for that promise
	if (installPromise) {
		return installPromise;
	}

	// Ensure THREE subset is initialized
	if (!threeSubset) {
		throw new Error('Camera controls THREE subset not initialized. Call initCameraControlsThree() first.');
	}

	// Start loading
	installPromise = (async () => {
		const module = await import(/* webpackChunkName: "camera-controls" */ 'camera-controls');
		const CameraControls = module.default;

		// Install with THREE subset (only happens once)
		CameraControls.install({ THREE: threeSubset });

		cachedCameraControls = CameraControls;
		return CameraControls;
	})();

	return installPromise;
};

/**
 * Check if camera-controls has been loaded
 */
export const isCameraControlsLoaded = (): boolean => {
	return cachedCameraControls !== null;
};

/**
 * Get the cached CameraControls class (throws if not loaded)
 * Use this only after ensuring loadCameraControls() has completed
 */
export const getCameraControls = (): CameraControlsType => {
	if (!cachedCameraControls) {
		throw new Error('CameraControls not loaded. Call loadCameraControls() first.');
	}
	return cachedCameraControls;
};
